/* eslint-disable @next/next/no-img-element */
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Button } from "../ui/button";
import { CircleCheck, Loader2, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Calendar } from "../ui/calendar";
import { OUTLINE_BRAND_BTN_CLASS, PRIMARY_BRAND_BTN_CLASS } from "./constants";
import { formatPrice, formatSubscriptionDateForDisplay } from "./formatters";
import {
  previewSubscriptionMergeShipping,
  requestSubscriptionMergeStayAi,
} from "@/lib/api";
import { toastSubscriptionApiError } from "./subscription-errors";
import { trackBuyAgainWidgetInteract } from "@/lib/tracking";
import {
  frequencyKey,
  frequencyLabel,
  formatShippingAddressOneLine,
  hasDifferingShippingAddresses,
  currentShippingCostTotal,
  largestMergeBucket,
  subscriptionDiscountBucketKey,
  type MergeableSubscription,
} from "./merge-eligibility";
import type { ShippingAddress } from "@/hooks/usePurchasedProductDetail";

const CUSTOM_DATE = "__custom__";

/** Local YYYY-MM-DD for `date`. */
function toLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Extract the YYYY-MM-DD part from an ISO / date string, or null. */
function extractYmd(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw.trim());
  if (m) return m[1];
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : toLocalYmd(new Date(t));
}

export interface MergeSubscriptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All active, same-frequency-eligible subscriptions to choose from. */
  subscriptions: MergeableSubscription[];
  customerEmail?: string | null;
  customerName?: string | null;
  trackingShop?: string | null;
  /** Called after a merge request is sent so the caller can suppress the banner. */
  onSubmitted?: () => void;
}

type Step = "select" | "date" | "review" | "success";

export function MergeSubscriptionsDialog({
  open,
  onOpenChange,
  subscriptions,
  customerEmail,
  customerName,
  trackingShop,
  onSubmitted,
}: MergeSubscriptionsDialogProps) {
  const [step, setStep] = useState<Step>("select");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [dateChoice, setDateChoice] = useState<string | null>(null);
  const [customDate, setCustomDate] = useState<string | null>(null);
  // Whether the backend combined immediately (vs. handing off to support) —
  // decides which success copy the customer sees.
  const [combineCompleted, setCombineCompleted] = useState(false);
  const queryClient = useQueryClient();

  const minYmd = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return toLocalYmd(t);
  }, []);

  // The group the banner advertised — same frequency, same destination. It is
  // what the customer came here to combine, so it starts selected; they can
  // uncheck any of it. Empty when nothing can actually combine.
  const defaultSelectedIds = useMemo(
    () => largestMergeBucket(subscriptions).map((s) => s.subscriptionId),
    [subscriptions],
  );

  // With exactly two subscriptions, and both combinable, the selection step is
  // a checkbox pair whose only valid answer is "both" — unchecking either
  // leaves nothing to combine. Skip it and open on the date. Any other shape
  // (a third subscription, or two that can't combine) is a real choice.
  const skipSelect =
    subscriptions.length === 2 && defaultSelectedIds.length === 2;
  const firstStep: Step = skipSelect ? "date" : "select";

  // Reset all wizard state whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setStep(firstStep);
      setSelectedIds(defaultSelectedIds);
      setDateChoice(null);
      setCustomDate(null);
      setCombineCompleted(false);
    }
  }, [open, defaultSelectedIds, firstStep]);

  // Frequency and discount pinned by the first selection. Differing addresses
  // stay selectable — the review step calls them out and the request flags them
  // for the team to confirm with the customer (locking those rows just reads as broken).
  const pinnedKey = useMemo(() => {
    const first = subscriptions.find((s) =>
      selectedIds.includes(s.subscriptionId),
    );
    return first ? frequencyKey(first.intervalUnit, first.intervalCount) : null;
  }, [subscriptions, selectedIds]);

  const pinnedDiscountKey = useMemo(() => {
    const first = subscriptions.find((s) =>
      selectedIds.includes(s.subscriptionId),
    );
    return first ? subscriptionDiscountBucketKey(first) : null;
  }, [subscriptions, selectedIds]);

  const selectedSubs = useMemo(
    () =>
      subscriptions.filter((s) => selectedIds.includes(s.subscriptionId)),
    [subscriptions, selectedIds],
  );

  const toggle = (sub: MergeableSubscription) => {
    setSelectedIds((prev) =>
      prev.includes(sub.subscriptionId)
        ? prev.filter((id) => id !== sub.subscriptionId)
        : [...prev, sub.subscriptionId],
    );
  };

  const isRowSelectable = (sub: MergeableSubscription): boolean =>
    mergeRowLockReason(sub, pinnedKey, pinnedDiscountKey) == null;

  const rowLockReason = (sub: MergeableSubscription) =>
    mergeRowLockReason(sub, pinnedKey, pinnedDiscountKey);

  // Distinct future next-billing dates across the selected subscriptions.
  const existingDates = useMemo(() => {
    const set = new Set<string>();
    for (const sub of selectedSubs) {
      const ymd = extractYmd(sub.nextBillingDate);
      if (ymd && ymd >= minYmd) set.add(ymd);
    }
    return Array.from(set).sort();
  }, [selectedSubs, minYmd]);

  // Entering the date step pre-selects the earliest billing date so Continue
  // is one click. Only when no future date exists does the choice fall back to
  // the calendar (CUSTOM_DATE), which starts empty and blocks Continue.
  useEffect(() => {
    if (step !== "date") return;
    setDateChoice((prev) => {
      if (existingDates.length === 0) return CUSTOM_DATE;
      return prev && existingDates.includes(prev) ? prev : existingDates[0];
    });
  }, [step, existingDates]);

  const resolvedDate =
    dateChoice === CUSTOM_DATE ? customDate : dateChoice;
  const dateSource: "existing" | "custom" =
    dateChoice === CUSTOM_DATE ? "custom" : "existing";

  const differingAddresses = useMemo(
    () => hasDifferingShippingAddresses(selectedSubs),
    [selectedSubs],
  );

  // Real merged-shipping quote for the exact selection, fetched on the review
  // step. Saving = what these subs pay today − the single merged-order fee.
  const previewQuery = useQuery({
    queryKey: ["merge-shipping-preview", selectedIds],
    queryFn: () => previewSubscriptionMergeShipping(selectedIds),
    enabled: step === "review" && selectedIds.length >= 2,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const savingsAmount = useMemo(() => {
    const merged = previewQuery.data?.mergedShippingPrice;
    if (merged == null) return null;
    const savings = currentShippingCostTotal(selectedSubs) - merged;
    return savings > 0.005 ? Math.round(savings * 100) / 100 : null;
  }, [previewQuery.data, selectedSubs]);

  const mergeMutation = useMutation({
    mutationFn: () => {
      if (!resolvedDate) throw new Error("Pick a delivery date first.");
      return requestSubscriptionMergeStayAi(
        {
          subscriptions: selectedSubs.map((s) => ({
            subscription_id: s.subscriptionId,
            product_titles: s.products.map((p) => p.title),
            next_billing_date: s.nextBillingDate,
            shipping_address: formatShippingAddressOneLine(s.shippingAddress),
          })),
          frequency_label: pinnedKey
            ? frequencyLabel(
                selectedSubs[0].intervalUnit,
                selectedSubs[0].intervalCount,
              )
            : "",
          delivery_date: resolvedDate,
          delivery_date_source: dateSource,
          differing_addresses: differingAddresses,
        },
        { customerEmail, customerName },
      );
    },
    onSuccess: (result) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract("subscription_merge_submit", trackingShop, {
          count: selectedSubs.length,
          frequency: pinnedKey ?? undefined,
          date_source: dateSource,
          differing_addresses: differingAddresses,
          completed: result.completed,
        });
      }
      if (result.completed) {
        // The subscriptions actually changed — refresh the list behind the
        // dialog so the combined subscription is what the customer returns to.
        void queryClient.invalidateQueries({
          queryKey: ["purchased-products"],
        });
      }
      setCombineCompleted(result.completed);
      onSubmitted?.();
      setStep("success");
    },
    onError: (error) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract("subscription_merge_failed", trackingShop, {
          count: selectedSubs.length,
          frequency: pinnedKey ?? undefined,
          date_source: dateSource,
          differing_addresses: differingAddresses,
        });
      }
      toastSubscriptionApiError(error);
    },
  });

  const pending = mergeMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
      >
        {step === "success" ? (
          <SuccessBody
            completed={combineCompleted}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <>
            <DialogHeader>
              <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
                <DialogTitle
                  className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  Combine Subscriptions
                </DialogTitle>
                <DialogClose
                  data-slot="dialog-close"
                  render={
                    <Button
                      variant="ghost"
                      className="tw:p-0 tw:m-0 tw:shrink-0"
                      size="icon-sm"
                      aria-label="Close"
                    />
                  }
                >
                  <X />
                </DialogClose>
              </div>
            </DialogHeader>

            {step === "date" && (
              <p
                className="tw:text-sm tw:my-2"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                Choose when you&apos;d like your combined order delivered.
              </p>
            )}

            <div className="tw:mt-2 tw:overflow-y-auto tw:min-h-0">
              {step === "select" && (
                <SelectStep
                  subscriptions={subscriptions}
                  selectedIds={selectedIds}
                  isRowSelectable={isRowSelectable}
                  rowLockReason={rowLockReason}
                  onToggle={toggle}
                />
              )}
              {step === "date" && (
                <DateStep
                  existingDates={existingDates}
                  dateChoice={dateChoice}
                  onChoose={setDateChoice}
                  customDate={customDate}
                  onCustomDate={setCustomDate}
                  minYmd={minYmd}
                />
              )}
              {step === "review" && (
                <ReviewStep
                  selectedSubs={selectedSubs}
                  deliveryDate={resolvedDate}
                  differingAddresses={differingAddresses}
                  shippingAddress={selectedSubs[0]?.shippingAddress ?? null}
                  currentShipping={currentShippingCostTotal(selectedSubs)}
                  mergedShipping={previewQuery.data?.mergedShippingPrice ?? null}
                  savingsAmount={savingsAmount}
                  savingsPending={previewQuery.isFetching}
                />
              )}
            </div>

            <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
              {step === "select" && (
                <Button
                  type="button"
                  disabled={selectedIds.length < 2}
                  onClick={() => setStep("date")}
                  className={PRIMARY_BRAND_BTN_CLASS}
                >
                  Continue
                </Button>
              )}
              {step === "date" && (
                <>
                  <Button
                    type="button"
                    disabled={!resolvedDate}
                    onClick={() => setStep("review")}
                    className={PRIMARY_BRAND_BTN_CLASS}
                  >
                    Continue
                  </Button>
                  {!skipSelect && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep("select")}
                      className={OUTLINE_BRAND_BTN_CLASS}
                    >
                      Back
                    </Button>
                  )}
                </>
              )}
              {step === "review" && (
                <>
                  <Button
                    type="button"
                    disabled={pending || !resolvedDate}
                    onClick={() => mergeMutation.mutate()}
                    className={PRIMARY_BRAND_BTN_CLASS}
                  >
                    {pending ? (
                      <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
                    ) : (
                      "Yes, combine them"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending}
                    onClick={() => onOpenChange(false)}
                    className={`${OUTLINE_BRAND_BTN_CLASS} tw:disabled:opacity-50`}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function mergeRowLockReason(
  sub: MergeableSubscription,
  pinnedKey: string | null,
  pinnedDiscountKey: string | null,
): "frequency" | "discount" | null {
  if (
    pinnedKey != null &&
    frequencyKey(sub.intervalUnit, sub.intervalCount) !== pinnedKey
  ) {
    return "frequency";
  }
  if (pinnedDiscountKey != null) {
    const dk = subscriptionDiscountBucketKey(sub);
    if (dk == null || dk !== pinnedDiscountKey) return "discount";
  }
  return null;
}

function SelectStep({
  subscriptions,
  selectedIds,
  isRowSelectable,
  rowLockReason,
  onToggle,
}: {
  subscriptions: MergeableSubscription[];
  selectedIds: number[];
  isRowSelectable: (sub: MergeableSubscription) => boolean;
  rowLockReason: (sub: MergeableSubscription) => "frequency" | "discount" | null;
  onToggle: (sub: MergeableSubscription) => void;
}) {
  return (
    <fieldset className="tw:flex tw:flex-col tw:gap-2 tw:border-0 tw:p-0 tw:m-0 tw:min-w-0">
      {subscriptions.map((sub) => {
        const selected = selectedIds.includes(sub.subscriptionId);
        const selectable = selected || isRowSelectable(sub);
        const lead = sub.products[0];
        const extra = sub.products.length - 1;
        const inputId = `merge-sub-${sub.subscriptionId}`;
        return (
          <label
            key={sub.subscriptionId}
            htmlFor={inputId}
            className={cn(
              "tw:flex tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-3 tw:text-sm tw:transition-colors",
              selectable
                ? "tw:cursor-pointer"
                : "tw:cursor-not-allowed tw:opacity-45",
              selected
                ? "tw:border-[#523D33] tw:bg-[#FDEDEE]/50"
                : "tw:border-gray-200 tw:bg-white",
              selectable && !selected && "hover:tw:bg-gray-50/80",
            )}
          >
            <input
              id={inputId}
              type="checkbox"
              checked={selected}
              disabled={!selectable}
              onChange={() => onToggle(sub)}
              className="tw:size-4.5 tw:shrink-0 tw:accent-[#523D33] tw:cursor-pointer"
            />
            {lead?.image_url ? (
              <img
                src={lead.image_url}
                alt=""
                className="tw:size-10 tw:shrink-0 tw:rounded tw:object-cover"
              />
            ) : null}
            <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
              <span
                className="tw:font-medium tw:leading-snug tw:truncate"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {lead?.title ?? `Subscription #${sub.subscriptionId}`}
                {extra > 0 ? ` +${extra} more` : ""}
              </span>
              <span className="tw:text-xs tw:text-[#523D33]/70 tw:leading-snug">
                {frequencyLabel(sub.intervalUnit, sub.intervalCount)}
                {sub.nextBillingDate
                  ? ` · next ${formatSubscriptionDateForDisplay(sub.nextBillingDate)}`
                  : ""}
              </span>
              {!selectable && (
                <span className="tw:text-[11px] tw:text-[#523D33]/60 tw:leading-snug">
                  {rowLockReason(sub) === "discount"
                    ? "Different discount"
                    : "Different frequency"}
                </span>
              )}
            </div>
          </label>
        );
      })}
    </fieldset>
  );
}

function DateStep({
  existingDates,
  dateChoice,
  onChoose,
  customDate,
  onCustomDate,
  minYmd,
}: {
  existingDates: string[];
  dateChoice: string | null;
  onChoose: (choice: string) => void;
  customDate: string | null;
  onCustomDate: (ymd: string) => void;
  minYmd: string;
}) {
  // No future billing date to offer — the calendar is the only way forward.
  if (existingDates.length === 0) {
    return (
      <div className="tw:rounded-md tw:border tw:border-gray-200 tw:p-3">
        <Calendar
          value={customDate ?? undefined}
          onSelect={onCustomDate}
          min={minYmd}
        />
      </div>
    );
  }
  return (
    <fieldset className="tw:flex tw:flex-col tw:gap-2 tw:border-0 tw:p-0 tw:m-0 tw:min-w-0">
      {existingDates.map((d) => {
        const selected = dateChoice === d;
        const inputId = `merge-date-${d}`;
        return (
          <label
            key={d}
            htmlFor={inputId}
            className={cn(
              "tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-3 tw:text-sm tw:transition-colors",
              selected
                ? "tw:border-[#523D33] tw:bg-[#FDEDEE]/50"
                : "tw:border-gray-200 tw:bg-white hover:tw:bg-gray-50/80",
            )}
          >
            <input
              id={inputId}
              type="radio"
              name="merge-date"
              checked={selected}
              onChange={() => onChoose(d)}
              className="tw:size-4.5 tw:shrink-0 tw:accent-[#523D33] tw:cursor-pointer"
            />
            <span
              className="tw:font-medium"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              {formatSubscriptionDateForDisplay(d)}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

/** Shipping amount for the summary — 0 (or unknown-free) reads as "Free". */
function formatShippingValue(amount: number): string {
  return amount <= 0.005 ? "Free" : `$${formatPrice(amount)}`;
}

function ReviewStep({
  selectedSubs,
  deliveryDate,
  differingAddresses,
  shippingAddress,
  currentShipping,
  mergedShipping,
  savingsAmount,
  savingsPending,
}: {
  selectedSubs: MergeableSubscription[];
  deliveryDate: string | null;
  differingAddresses: boolean;
  shippingAddress: ShippingAddress | null;
  currentShipping: number;
  mergedShipping: number | null;
  savingsAmount: number | null;
  savingsPending: boolean;
}) {
  // With differing addresses there is no single address to show; the note
  // below explains the address will be confirmed instead.
  const addressLine = differingAddresses
    ? null
    : formatShippingAddressOneLine(shippingAddress);
  const hasSavings = savingsAmount != null && savingsAmount > 0;
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div className="tw:rounded-md tw:border tw:border-gray-200 tw:p-3">
        <p className="tw:m-0 tw:mb-2 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-[#523D33]/70">
          Combining {selectedSubs.length} subscriptions
        </p>
        <ul className="tw:m-0 tw:p-0 tw:list-none tw:flex tw:flex-col tw:gap-1">
          {selectedSubs.map((sub) => {
            const lead = sub.products[0];
            const extra = sub.products.length - 1;
            return (
              <li
                key={sub.subscriptionId}
                className="tw:text-sm tw:leading-snug"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {lead?.title ?? `Subscription #${sub.subscriptionId}`}
                {extra > 0 ? ` +${extra} more` : ""}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="tw:rounded-md tw:border tw:border-gray-200 tw:p-3">
        <p className="tw:m-0 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-[#523D33]/70">
          Delivery date
        </p>
        <p
          className="tw:m-0 tw:mt-1 tw:text-sm tw:font-medium"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {deliveryDate
            ? formatSubscriptionDateForDisplay(deliveryDate)
            : "—"}
        </p>
      </div>
      {addressLine && (
        <div className="tw:rounded-md tw:border tw:border-gray-200 tw:p-3">
          <p className="tw:m-0 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-[#523D33]/70">
            Shipping address
          </p>
          <p
            className="tw:m-0 tw:mt-1 tw:text-sm tw:leading-snug"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            {addressLine}
          </p>
        </div>
      )}
      {differingAddresses && (
        <p className="tw:m-0 tw:text-xs tw:leading-relaxed tw:text-[#523D33]/80">
          These subscriptions ship to different addresses — we&apos;ll confirm
          the delivery address with you.
        </p>
      )}
      {savingsPending ? (
        <div className="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-[#523D33]/70">
          <Loader2 className="tw:size-3.5 tw:animate-spin" />
          Calculating shipping savings…
        </div>
      ) : mergedShipping != null ? (
        <div className="tw:flex tw:flex-col tw:gap-1.5 tw:rounded-md tw:border tw:border-[#523D33]/15 tw:bg-[#FDEDEE]/50 tw:p-3">
          <div className="tw:flex tw:items-center tw:justify-between tw:text-sm tw:text-[#523D33]/80">
            <span>Current shipping</span>
            <span
              className={
                hasSavings ? "tw:line-through tw:text-[#523D33]/50" : "tw:font-medium"
              }
            >
              {formatShippingValue(currentShipping)}
            </span>
          </div>
          <div className="tw:flex tw:items-center tw:justify-between tw:text-sm tw:text-[#523D33]/80">
            <span>New shipping</span>
            <span className="tw:font-medium tw:text-[#523D33]">
              {formatShippingValue(mergedShipping)}
            </span>
          </div>
          {hasSavings && (
            <div className="tw:mt-1 tw:flex tw:items-center tw:justify-between tw:border-t tw:border-[#523D33]/10 tw:pt-2">
              <span className="tw:text-sm tw:font-bold tw:text-[#523D33]">
                You save
              </span>
              <span className="tw:text-base tw:font-extrabold tw:text-[#523D33]">
                ${formatPrice(savingsAmount!)}
              </span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function SuccessBody({
  completed,
  onClose,
}: {
  /** True when the combine already ran; false when support will finish it. */
  completed: boolean;
  onClose: () => void;
}) {
  return (
    <>
      <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-4 tw:text-center tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-300">
        <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3 tw:mb-4">
          <CircleCheck
            className="tw:size-10 tw:text-[#523D33]"
            strokeWidth={1.5}
          />
        </div>
        <p
          className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-2"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {completed ? "All combined!" : "Your request is in"}
        </p>
        <p className="tw:text-sm tw:m-0 tw:text-[#523D33]/80">
          {completed
            ? "Your subscriptions are now one — everything will arrive together in a single order."
            : "We'll combine your subscriptions shortly."}
        </p>
      </div>
      <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-2 tw:pt-2">
        <Button type="button" onClick={onClose} className={PRIMARY_BRAND_BTN_CLASS}>
          OK
        </Button>
      </DialogFooter>
    </>
  );
}
